import React from 'react';
import { MapPin } from 'lucide-react';

/**
 * Horizontal bar chart showing ARR distribution by region
 */
const RegionBarChart = ({ data, viewMode = 'pipeline' }) => {
  const isPipeline = viewMode === 'pipeline';
  const title = isPipeline ? 'Pipeline ARR by Region' : 'Won ARR by Region';

  // Region labels mapping
  const regionLabels = {
    'US': 'United States',
    'Europe': 'Europe',
    'UK': 'United Kingdom',
    'APAC': 'Asia Pacific',
    'LATAM': 'Latin America',
    'MENA': 'Middle East & North Africa',
    'ANZ': 'Australia & NZ',
    'Unknown': 'Unknown'
  };

  // Colors for regions
  const regionColors = {
    'US': { bg: 'bg-blue-500', light: 'bg-blue-100' },
    'Europe': { bg: 'bg-emerald-500', light: 'bg-emerald-100' },
    'UK': { bg: 'bg-purple-500', light: 'bg-purple-100' },
    'APAC': { bg: 'bg-orange-500', light: 'bg-orange-100' },
    'LATAM': { bg: 'bg-pink-500', light: 'bg-pink-100' },
    'MENA': { bg: 'bg-amber-500', light: 'bg-amber-100' },
    'ANZ': { bg: 'bg-cyan-500', light: 'bg-cyan-100' },
    'Unknown': { bg: 'bg-slate-400', light: 'bg-slate-100' }
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <MapPin size={20} className="text-blue-500" />
          {title}
        </h3>
        <div className="text-center py-8 text-slate-500">
          No regional data available
        </div>
      </div>
    );
  }

  // Transform data based on view mode
  const chartData = data.map(row => ({
    region: row.region,
    label: regionLabels[row.region] || row.region,
    arr: isPipeline ? row.pipelineARR : row.wonARR,
    count: isPipeline ? row.pipelineCount : row.wonCount,
    colors: regionColors[row.region] || regionColors['Unknown']
  })).filter(row => row.arr > 0).sort((a, b) => b.arr - a.arr);

  const maxARR = Math.max(...chartData.map(d => d.arr), 1);
  const totalARR = chartData.reduce((sum, d) => sum + d.arr, 0);

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <MapPin size={20} className="text-blue-500" />
          {title}
        </h3>
        <div className="text-sm text-slate-500">
          Total: <span className="font-semibold text-slate-700">{formatCurrency(totalARR)}</span>
        </div>
      </div>

      <div className="space-y-3">
        {chartData.map((item) => {
          const percentage = totalARR > 0 ? ((item.arr / totalARR) * 100).toFixed(1) : 0;
          const barWidth = (item.arr / maxARR) * 100;

          return (
            <div key={item.region} className="group">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${item.colors.bg}`}></span>
                  <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  <span className="text-xs text-slate-400">({item.count} deals)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{formatCurrency(item.arr)}</span>
                  <span className="text-xs text-slate-500 w-12 text-right">{percentage}%</span>
                </div>
              </div>
              <div className={`h-6 rounded-lg ${item.colors.light} overflow-hidden`}>
                <div
                  className={`h-full ${item.colors.bg} rounded-lg transition-all duration-500 ease-out group-hover:opacity-80`}
                  style={{ width: `${barWidth}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {chartData.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          No {isPipeline ? 'pipeline' : 'won'} deals in selected period
        </div>
      )}
    </div>
  );
};

export default RegionBarChart;
